import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, X, Loader2 } from 'lucide-react';
import Button from './Button';
export { default as Skeleton } from './Skeleton';

// ═══════════════════════════════════════════
//  STATUS TAG — premium micro-badge
// ═══════════════════════════════════════════
const categoryStyles = {
  todo: 'bg-blue-50 text-blue-700 border-blue-200/60',
  'in-progress': 'bg-amber-50 text-amber-700 border-amber-200/60',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200/60',
};

export const StatusTag = ({
  name,
  color,
  category,
  size = 'md',
  dot = true,
  className = '',
}) => {
  const hasColor = color && color.startsWith('#');
  const sizeClass = size === 'sm' ? 'badge-sm' : 'badge';

  if (hasColor) {
    return (
      <span
        className={`${sizeClass} border ${className}`}
        style={{
          backgroundColor: color + '10',
          color: color,
          borderColor: color + '20',
        }}
      >
        {dot && <span className="badge-dot" style={{ backgroundColor: color }} />}
        {name}
      </span>
    );
  }

  return (
    <span className={`${sizeClass} border ${categoryStyles[category] || 'bg-surface-100 text-surface-600 border-surface-200/60'} ${className}`}>
      {dot && <span className={`badge-dot ${category === 'todo' ? 'bg-blue-500' : category === 'in-progress' ? 'bg-amber-500' : category === 'done' ? 'bg-emerald-500' : 'bg-surface-400'}`} />}
      {name}
    </span>
  );
};

// ═══════════════════════════════════════════
//  TOAST — premium glass notification
// ═══════════════════════════════════════════
const toastIcons = {
  success: <CheckCircle size={16} className="text-success-500" />,
  error: <XCircle size={16} className="text-danger-500" />,
  warning: <AlertTriangle size={16} className="text-warning-500" />,
  info: <Info size={16} className="text-primary-900" />,
  loading: <Loader2 size={16} className="text-primary-900 animate-spin" />,
};

const toastBarStyles = {
  success: { background: 'linear-gradient(90deg, #22c55e, #16a34a)' },
  error: { background: 'linear-gradient(90deg, #ef4444, #dc2626)' },
  warning: { background: 'linear-gradient(90deg, #f59e0b, #d97706)' },
  info: { background: 'linear-gradient(135deg, #2a2a72, #1338be)' },
  loading: { background: 'linear-gradient(135deg, #2a2a72, #1338be)' },
};

let toastId = 0;
let toastListeners = [];

export const toast = {
  show: (message, options = {}) => {
    const id = ++toastId;
    const t = {
      id, message,
      type: options.type || 'info',
      duration: options.duration ?? (options.type === 'error' ? 6000 : 4000),
      title: options.title,
      action: options.action,
    };
    toastListeners.forEach((fn) => fn({ type: 'ADD', toast: t }));
    return id;
  },
  success: (msg, opts) => toast.show(msg, { ...opts, type: 'success' }),
  error: (msg, opts) => toast.show(msg, { ...opts, type: 'error', duration: opts?.duration ?? 6000 }),
  warning: (msg, opts) => toast.show(msg, { ...opts, type: 'warning' }),
  info: (msg, opts) => toast.show(msg, { ...opts, type: 'info' }),
  loading: (msg, opts) => toast.show(msg, { ...opts, type: 'loading', duration: opts?.duration ?? 30000 }),
  dismiss: (id) => toastListeners.forEach((fn) => fn({ type: 'REMOVE', id })),
};

export const ToastProvider = ({ position = 'top-right' }) => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const listener = (action) => {
      if (action.type === 'ADD') setToasts((prev) => [...prev.slice(-4), action.toast]);
      if (action.type === 'REMOVE') setToasts((prev) => prev.filter((t) => t.id !== action.id));
    };
    toastListeners.push(listener);
    return () => { toastListeners = toastListeners.filter((l) => l !== listener); };
  }, []);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-[9999] flex flex-col gap-2.5 pointer-events-none`}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => toast.dismiss(t.id)} />
      ))}
    </div>
  );
};

const ToastItem = ({ toast: t, onDismiss }) => {
  useEffect(() => {
    if (t.duration > 0) {
      const timer = setTimeout(onDismiss, t.duration);
      return () => clearTimeout(timer);
    }
  }, [t.duration, onDismiss]);

  return (
    <div
      className="pointer-events-auto w-80 bg-white shadow-dropdown border border-surface-200/40 overflow-hidden animate-slide-right"
      style={{ borderRadius: '16px' }}
    >
      <div className="h-[2.5px]" style={toastBarStyles[t.type]} />
      <div className="flex items-start gap-3 px-4 py-3">
        <span className="mt-0.5 flex-shrink-0">{toastIcons[t.type]}</span>
        <div className="flex-1 min-w-0">
          {t.title && <p className="text-[13px] font-semibold text-surface-900 mb-0.5">{t.title}</p>}
          <p className="text-[12.5px] text-surface-600 leading-snug">{t.message}</p>
          {t.action && (
            <button onClick={t.action.onClick}
              className="text-[11px] font-bold text-primary-900 hover:text-primary-700 mt-1.5 transition-colors">
              {t.action.label}
            </button>
          )}
        </div>
        <button onClick={onDismiss}
          className="flex-shrink-0 p-1 text-surface-500 hover:text-surface-600 rounded-[8px] hover:bg-surface-100 transition-all">
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
//  CONFIRM DIALOG — premium glass panel
// ═══════════════════════════════════════════
export const ConfirmDialog = ({
  open = false,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
  icon: CustomIcon,
}) => {
  const iconMap = {
    danger: <AlertTriangle size={20} className="text-danger-500" />,
    warning: <AlertTriangle size={20} className="text-warning-500" />,
    info: <Info size={20} className="text-primary-900" />,
  };

  const bgMap = {
    danger: 'bg-danger-50',
    warning: 'bg-warning-50',
    info: 'bg-primary-50',
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-surface-950/25 backdrop-blur-[8px] animate-modal-overlay" onClick={onClose} />
      <div
        className="relative bg-white shadow-modal border border-surface-200/40 w-full max-w-xs animate-modal-content p-6"
        style={{ borderRadius: 'var(--radius-modal)' }}
      >
        <div className="flex items-start gap-3.5">
          <div className={`w-11 h-11 rounded-[14px] ${bgMap[variant]} flex items-center justify-center flex-shrink-0`}>
            {CustomIcon || iconMap[variant]}
          </div>
          <div className="flex-1 pt-0.5">
            <h3 className="text-[14px] font-bold text-surface-900">{title}</h3>
            <p className="text-[12.5px] text-surface-600 mt-1 leading-relaxed">{description}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 mt-6">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : variant === 'warning' ? 'accent' : 'primary'}
            size="sm"
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
